import { Component, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { NavigationService, NavCrumb } from '../../core/services/navigation.service';
import { StoreSwitcherComponent } from '../../shared/components/store-switcher/store-switcher.component';
import { UserMenuComponent } from '../../shared/components/user-menu/user-menu.component';

/**
 * Fixed top bar: breadcrumb for the active page on the left, store switcher
 * and user menu on the right. Branding lives in the sidebar rail.
 */
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, StoreSwitcherComponent, UserMenuComponent],
  templateUrl: './header.component.html',
})
export class HeaderComponent {
  private navService = inject(NavigationService);
  private router = inject(Router);
  private authService = inject(AuthService);

  crumbs = signal<NavCrumb[]>([]);

  /** Workspace a super admin has entered (null at platform level). */
  tenant = toSignal(this.authService.selectedTenant$, { initialValue: this.authService.getSelectedTenant() });
  leaving = signal(false);

  /** Signed-in user's own client; shown when no super-admin workspace chip is. */
  user = toSignal(this.authService.user$, { initialValue: null as any });

  constructor() {
    this.refresh();
    // Users signed in before clientName was added to /auth/me: refresh once.
    const stored = this.user();
    if (stored?.clientId && stored.clientName === undefined) {
      this.authService.refreshUser().subscribe({ error: () => {} });
    }
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.refresh());
  }

  private refresh(): void {
    this.crumbs.set(this.navService.getBreadcrumb());
  }

  /** Leave the workspace and return to the platform-level Super Admin screens. */
  exitTenant(): void {
    if (this.leaving()) return;
    this.leaving.set(true);
    const wasTemplate = !!this.tenant()?.isTemplate;
    this.authService.setSelectedTenant(null);
    const target = wasTemplate ? '#/super-admin/industry-templates' : '#/super-admin/clients';
    this.authService.refreshUser().subscribe({
      next: () => {
        window.location.href = window.location.pathname + target;
        window.location.reload();
      },
      error: () => {
        window.location.href = window.location.pathname + target;
        window.location.reload();
      },
    });
  }
}
